import React, { useEffect, useState, useCallback, useRef } from 'react';
import ModalDialog from '../UI/Dialog/ModalDialog';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import Grid from '@mui/material/Grid';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import ListItemText from '@mui/material/ListItemText';
import List from '@mui/material/List';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import PrinterIcon from '@mui/icons-material/PrintTwoTone';
import Typography from '@mui/material/Typography';
import SelectField from '../UI/SelectField/SelectField';
import ValueHelper from '../../helpers/valueHelper';
import common from '../../jsons/common.json';
import { toggleDeviceDialog } from '../../actions/devices';
import TextField from '../UI/TextField/TextField';
import isEmpty from '../../is-empty';
import { DeviceHelper } from '../../helpers/deviceHelper';
import CheckboxField from '../UI/CheckboxField/CheckboxField';
import IconButton from '@mui/material/IconButton';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import Divider from '@mui/material/Divider';
import CircularProgress from '../UI/Progress/EvoraSpinner';
import CometSpinner from '../UI/Progress/CometSpinner';
import Box from '@mui/material/Box';
import DeviceDiagnostics from './DeviceDiagnostics';

const DEVICETEMPLATE = {
  name: '',
  deviceType: '',
  uid: '',
  connection: '',
  manufacturer: '',
  provider: '',
  version: '',
  quality: '',
  qualitySel: '',
  count: 1,
  pauseCount: 0,
  labels: [],
  status: 'template'
};

const statusColor = (status) => {
  switch (status) {
    case 'new': return 'success.main';
    case 'registered': return 'primary.main';
    case 'disconnected': return 'text.disabled';
    default: return 'text.secondary';
  }
};

const statusDotColor = (deviceStatus, resolutionMismatch) => {
  if (!deviceStatus) return 'text.disabled';
  if (deviceStatus.isReadyToPrint === false) return 'error.main';
  // Ready, but the configured quality disagrees with the printer's head — labels
  // will print at the wrong scale, so flag it on the readiness dot.
  if (deviceStatus.isReadyToPrint === true && resolutionMismatch) return 'warning.main';
  if (deviceStatus.isReadyToPrint === true) return 'success.main';
  return 'warning.main';
};

const dpmmToDpi = (qualityLabel) => {
  if (!qualityLabel) return null;
  const match = qualityLabel.match(/(\d+)\s*dpmm/i);
  if (!match) return null;
  const dpmm = parseInt(match[1]);
  const raw = dpmm * 25.4;
  const supported = [203, 300, 600];
  return supported.reduce((best, dpi) =>
    Math.abs(dpi - raw) < Math.abs(best - raw) ? dpi : best
  );
};

// Pull the dpmm number out of a quality value label, e.g. "12dpmm (300 dpi)" -> 12
const qualityLabelToDpmm = (qualityLabel) => {
  const match = qualityLabel != null ? qualityLabel.match(/(\d+)\s*dpmm/i) : null;
  return match != null ? parseInt(match[1]) : null;
};

// Find the labelPrintQualities option matching a detected head resolution
const qualityOptionForDpmm = (qualities, dpmm) =>
  (qualities || []).find(q => qualityLabelToDpmm(q.label) === dpmm);

const DeviceManager = () => {
  const dispatch = useDispatch();
  const hasRegisteredDevices = useSelector(state => state.devices.hasRegisteredDevices);
  const authConstants = useSelector(state => state.auth.constants);
  const authTenant = useSelector(state => state.auth.user.tenant);
  const newDevices = useSelector(state => state.devices.new);
  const deviceList = useSelector(state => state.devices.registered);
  const discoDevices = useSelector(state => state.devices.notConnected);
  const openDialog = useSelector(state => state.devices.deviceDialogOpen);
  const [enableSave, setEnableSave] = useState(true);
  const { t } = useTranslation();
  const [values, setValues] = useState({
    labelPrintQualities: [],
    labelPrintTypes: [],
    templatesByType: {},
  });
  const [errors, setErrors] = useState({});
  const [selectedDevice, setSelectedDevice] = useState({ ...DEVICETEMPLATE });
  const [deviceStatuses, setDeviceStatuses] = useState({});
  const statusCheckRef = useRef(0);

  useEffect(() => {
    async function fetchData() {
      const { labelPrintQualities, labelPrintTypes } = await ValueHelper.getCachedValues([
        common.cacheValues.labelPrintQuality, common.cacheValues.labelPrintType
      ], authConstants, null, authTenant);

      // Fetch templates for each label type
      const templatesByType = {};
      if (labelPrintTypes && labelPrintTypes.length > 0) {
        await Promise.all(labelPrintTypes.map(async (type) => {
          try {
            const res = await axios.get('/api/labeltemplate/type/' + type.value);
            templatesByType[type.value] = res.data || [];
          } catch {
            templatesByType[type.value] = [];
          }
        }));
      }

      setValues({
        labelPrintQualities: labelPrintQualities,
        labelPrintTypes: labelPrintTypes,
        templatesByType: templatesByType,
      });
    }
    fetchData();
  }, [authConstants, authTenant]);

  const checkAllDeviceStatuses = useCallback(async (allDevices) => {
    const runId = ++statusCheckRef.current;
    const connected = allDevices.filter(d => d._sdkDevice || typeof d.send === 'function');
    if (connected.length === 0) return;
    await Promise.allSettled(connected.map(async (device) => {
      const status = await DeviceHelper.getDeviceStatus(device);
      if (statusCheckRef.current !== runId) return;
      setDeviceStatuses(prev => ({ ...prev, [device.uid]: status }));
      // A printer that didn't answer the readiness check won't answer a
      // resolution query either — skip it instead of waiting out more timeouts.
      if (status?.isReadyToPrint == null && /timeout/i.test(status?.status || '')) return;
      // Query the head resolution sequentially after the status check so the
      // two send/read exchanges on the same device can't interleave.
      const resolution = await DeviceHelper.getPrinterResolution(device);
      if (statusCheckRef.current !== runId) return;
      setDeviceStatuses(prev => ({ ...prev, [device.uid]: { ...prev[device.uid], resolution: resolution } }));
    }));
  }, []);

  const refreshDevices = useCallback(async () => {
    setEnableSave(false);
    setDeviceStatuses({});
    let devices = await DeviceHelper.getDevices();
    setEnableSave(true);
    dispatch({ type: 'SET_ZEBRA_PRINTERS', payload: { printers: devices } });
    if (devices.hasRegisteredDevices !== hasRegisteredDevices)
      dispatch({ type: 'HAS_DEVICES', payload: devices.hasRegisteredDevices });
    // Auto-check status for all connected devices
    const allConnected = [...(devices.new || []), ...(devices.registered || [])];
    checkAllDeviceStatuses(allConnected);
  }, [hasRegisteredDevices, dispatch, checkAllDeviceStatuses]);

  useEffect(() => {
    if (openDialog) {
      refreshDevices();
    } else {
      setDeviceStatuses({});
      setSelectedDevice({ ...DEVICETEMPLATE });
    }
  }, [openDialog, refreshDevices]);

  const changeBool = useCallback((labelType, prop) => {
    setSelectedDevice(prev => ({
      ...prev,
      labels: (prev.labels || []).map(label =>
        label.type === labelType ? { ...label, [prop]: !label[prop] } : label
      )
    }));
  }, []);

  const changeLabelValue = useCallback((labelType, prop, value) => {
    setSelectedDevice(prev => ({
      ...prev,
      labels: (prev.labels || []).map(label =>
        label.type === labelType ? { ...label, [prop]: value } : label
      )
    }));
  }, []);

  const changeValue = useCallback((e) => {
    const { name, value } = e.target;
    setSelectedDevice(prev => ({ ...prev, [name]: value }));
  }, []);

  const changeQuality = useCallback((e) => {
    setSelectedDevice(prev => ({ ...prev, quality: e.target.value, qualitySel: e }));
  }, []);

  // True when a device's configured quality disagrees with the head resolution
  // the printer reported during the readiness sweep.
  const hasResolutionMismatch = useCallback((device) => {
    const resolution = deviceStatuses[device.uid]?.resolution;
    const qualityItem = values.labelPrintQualities?.find(q => q.value === device.quality);
    const dpmm = qualityLabelToDpmm(qualityItem?.label);
    return resolution != null && dpmm != null && dpmm !== resolution.dpmm;
  }, [deviceStatuses, values.labelPrintQualities]);

  const detectedResolution = deviceStatuses[selectedDevice.uid]?.resolution;
  const resolutionMismatch = hasResolutionMismatch(selectedDevice);

  // When the printer reports its head resolution and no quality has been picked
  // yet (e.g. a freshly connected device), pre-select the matching quality.
  useEffect(() => {
    if (detectedResolution == null) return;
    const option = qualityOptionForDpmm(values.labelPrintQualities, detectedResolution.dpmm);
    if (option == null) return;
    setSelectedDevice(prev => {
      if (prev.uid !== selectedDevice.uid || !isEmpty(prev.quality)) return prev;
      return { ...prev, quality: option.value, qualitySel: option };
    });
  }, [detectedResolution, selectedDevice.uid, values.labelPrintQualities]);

  const applyDetectedQuality = useCallback(() => {
    if (detectedResolution == null) return;
    const option = qualityOptionForDpmm(values.labelPrintQualities, detectedResolution.dpmm);
    if (option == null) return;
    setSelectedDevice(prev => ({ ...prev, quality: option.value, qualitySel: option }));
  }, [detectedResolution, values.labelPrintQualities]);

  const cancelDevice = useCallback(() => {
    setSelectedDevice({ ...DEVICETEMPLATE });
  }, []);

  const selectDevice = useCallback((event, device, status) => {
    if (device == null) {
      setSelectedDevice({ ...DEVICETEMPLATE });
      return;
    }
    let labels = device.labels;
    if ((labels == null || labels.length === 0) &&
        values.labelPrintTypes != null && values.labelPrintTypes.length > 0) {
      labels = values.labelPrintTypes.map(label => ({
        count: 1,
        eachPlant: false,
        labelPreview: false,
        promptForCount: false,
        type: label.value
      }));
    }
    // For new devices, the device IS the SDK Device object — spreading strips prototype
    // methods (send/read). Preserve the SDK reference so status check and printing work.
    const sdkRef = device._sdkDevice || (typeof device.send === 'function' ? device : null);
    setSelectedDevice({ ...device, labels: labels, status: status, _sdkDevice: sdkRef });
  }, [values.labelPrintTypes]);

  const selectLabel = useCallback((e, type) => {
    setSelectedDevice(prev => {
      const labels = isEmpty(prev.labels) ? [] : [...prev.labels];
      if (e.target.value != null) {
        const index = labels.findIndex(x => x.type === type);
        const entry = { type: type, templateId: e.target.value };
        if (index === -1) {
          labels.push(entry);
        } else {
          labels[index] = { ...labels[index], ...entry };
        }
      }
      return { ...prev, labels: labels };
    });
  }, []);

  const validation = useCallback(() => {
    let errs = {};
    if (isEmpty(selectedDevice.name)) {
      errs[t('name')] = t('required');
    }
    if (isEmpty(selectedDevice.quality)) {
      errs[t('quality')] = t('required');
    }
    if (isEmpty(selectedDevice.labels)) {
      errs[t('labels')] = t('required');
    }
    setErrors(errs);
    return isEmpty(errs);
  }, [selectedDevice, t]);

  const saveDevice = useCallback(async () => {
    setEnableSave(false);
    try {
      if (!validation()) {
        setEnableSave(true);
        return;
      }
      const result = await axios.post('/api/device', selectedDevice);
      setSelectedDevice(prev => ({ ...prev, _id: result.data._id }));
      let devices = await DeviceHelper.getDevices();
      dispatch({ type: 'SET_ZEBRA_PRINTERS', payload: { printers: devices } });
      if (devices.hasRegisteredDevices !== hasRegisteredDevices)
        dispatch({ type: 'HAS_DEVICES', payload: devices.hasRegisteredDevices });
      dispatch({ type: 'CREATE_ALERT', payload: { message: result.data.message, title: t('success'), severity: 'success' } });
    } catch (err) {
      dispatch({ type: 'CREATE_ALERT', payload: { message: err.response.data, title: t('error'), severity: 'error' } });
    }
    setEnableSave(true);
  }, [selectedDevice, hasRegisteredDevices, dispatch, t, validation]);

  const deleteClick = useCallback(async (event, device) => {
    try {
      let result = await axios.delete('/api/device/' + device._id);
      if (result.status === 200) {
        dispatch({ type: 'CREATE_ALERT', payload: { message: result.data.message, title: t('success'), severity: 'success' } });
        setEnableSave(false);
        let devices = await DeviceHelper.getDevices();
        setEnableSave(true);
        dispatch({ type: 'SET_ZEBRA_PRINTERS', payload: { printers: devices } });
      } else {
        dispatch({ type: 'CREATE_ALERT', payload: { message: result.data.message, title: t('error'), severity: 'error' } });
      }
    } catch (err) {
      dispatch({ type: 'CREATE_ALERT', payload: { message: err.response.data.message, title: t('error'), severity: 'error' } });
    }
  }, [dispatch, t]);

  const titleActions = (
    <IconButton aria-label="refresh" onClick={refreshDevices} disabled={!enableSave}
      sx={{ color: '#38546D' }}>
      {!enableSave ? <CometSpinner stroke="inherit" size={20} /> : <RefreshIcon />}
    </IconButton>
  );

  const isSelected = (device) =>
    device.uid === selectedDevice.uid && device.name === selectedDevice.name;

  const deviceSecondary = (device) => {
    const addr = device.connection && device.connection !== '' ?
      device.connection + ' - ' + device.uid : device.uid;
    const status = deviceStatuses[device.uid];
    return (
      <Box component='span' sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Box component='span' sx={{
          display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
          bgcolor: statusDotColor(status, hasResolutionMismatch(device)), flexShrink: 0
        }} />
        <span>{addr}</span>
      </Box>
    );
  };

  const newCount = newDevices?.length || 0;
  const regCount = deviceList?.length || 0;
  const discoCount = discoDevices?.length || 0;
  const totalDevices = newCount + regCount + discoCount;
  const hasDetail = selectedDevice.status !== 'template';
  const isConnected = selectedDevice._sdkDevice != null;
  // Diagnostics share the printer channel with the open-dialog status sweep;
  // wait for the sweep so replies can't cross
  const selectedStatus = deviceStatuses[selectedDevice.uid];
  const sweepDone = selectedStatus != null &&
    ('resolution' in selectedStatus || /timeout/i.test(selectedStatus.status || ''));
  const assignedTemplates = (selectedDevice.labels || [])
    .map(lbl => (values.templatesByType[lbl.type] || []).find(tpl => tpl._id === lbl.templateId))
    .filter(Boolean);

  return (
    <ModalDialog titleActions={titleActions} title={t('deviceManager')} toggleDialog={() => dispatch(toggleDeviceDialog(!openDialog))}
      dialogStatus={openDialog} fullWidth maxWidth='md'>
      <Grid container spacing={2}>
        <Grid size={5}>
          {!enableSave && totalDevices === 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}
          {enableSave && totalDevices === 0 && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <PrinterIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
              <Typography variant='body2' color='text.secondary'>{t('noDevicesFound')}</Typography>
            </Box>
          )}
          <List sx={{ width: '100%' }} dense>
            {newCount > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, pt: 1, pb: 0.5 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'success.main' }} />
                <Typography variant='subtitle2' color='text.secondary' sx={{ flex: 1 }}>{t('newDevices')}</Typography>
                <Chip label={newCount} size='small' color='success' variant='outlined' />
              </Box>
            )}
            {newDevices != null && newDevices.length > 0 &&
              newDevices.map((device, index) => (
                <ListItemButton key={'newDevice-' + index} onClick={(e) => selectDevice(e, device, 'new')}
                  sx={{
                    borderRadius: 1, mb: 0.5, mx: 0.5,
                    bgcolor: isSelected(device) ? 'action.selected' : 'transparent',
                    '&:hover': { bgcolor: 'action.hover' }
                  }}>
                  <ListItemAvatar>
                    {device.deviceType === 'printer' ?
                      <PrinterIcon sx={{ color: isSelected(device) ? 'secondary.main' : 'success.main', fontSize: '2rem' }} /> :
                      null
                    }
                  </ListItemAvatar>
                  <ListItemText
                    primary={device.name}
                    secondary={deviceSecondary(device)}
                    primaryTypographyProps={{ variant: 'body2', fontWeight: isSelected(device) ? 600 : 400 }}
                    secondaryTypographyProps={{ variant: 'caption', component: 'div' }}
                  />
                </ListItemButton>
              ))
            }
            {regCount > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, pt: 1.5, pb: 0.5 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'primary.main' }} />
                <Typography variant='subtitle2' color='text.secondary' sx={{ flex: 1 }}>{t('registered')}</Typography>
                <Chip label={regCount} size='small' color='primary' variant='outlined' />
              </Box>
            )}
            {deviceList != null && deviceList.length > 0 &&
              deviceList.map((device, index) => (
                <ListItemButton key={'regDevice-' + index} onClick={(e) => selectDevice(e, device, 'registered')}
                  sx={{
                    borderRadius: 1, mb: 0.5, mx: 0.5,
                    bgcolor: isSelected(device) ? 'action.selected' : 'transparent',
                    '&:hover': { bgcolor: 'action.hover' }
                  }}>
                  <ListItemAvatar>
                    {device.deviceType === 'printer' ?
                      <PrinterIcon sx={{ color: isSelected(device) ? 'secondary.main' : 'primary.main', fontSize: '2rem' }} /> :
                      null
                    }
                  </ListItemAvatar>
                  <ListItemText
                    primary={device.name}
                    secondary={deviceSecondary(device)}
                    primaryTypographyProps={{ variant: 'body2', fontWeight: isSelected(device) ? 600 : 400 }}
                    secondaryTypographyProps={{ variant: 'caption', component: 'div' }}
                  />
                  <ListItemSecondaryAction>
                    <IconButton size='small' onClick={(e) => deleteClick(e, device)}>
                      <DeleteIcon fontSize='small' />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItemButton>
              ))
            }
            {discoCount > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, pt: 1.5, pb: 0.5 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'text.disabled' }} />
                <Typography variant='subtitle2' color='text.secondary' sx={{ flex: 1 }}>{t('disconnected')}</Typography>
                <Chip label={discoCount} size='small' variant='outlined' />
              </Box>
            )}
            {discoDevices != null && discoDevices.length > 0 &&
              discoDevices.map((device, index) => (
                <ListItemButton key={'discoDevice-' + index} onClick={(e) => selectDevice(e, device, 'disconnected')}
                  sx={{
                    borderRadius: 1, mb: 0.5, mx: 0.5, opacity: 0.7,
                    bgcolor: isSelected(device) ? 'action.selected' : 'transparent',
                    '&:hover': { bgcolor: 'action.hover' }
                  }}>
                  <ListItemAvatar>
                    {device.deviceType === 'printer' ?
                      <PrinterIcon sx={{ color: 'text.disabled', fontSize: '2rem' }} /> :
                      null
                    }
                  </ListItemAvatar>
                  <ListItemText
                    primary={device.name}
                    secondary={device.connection && device.connection !== '' ?
                      device.connection + ' - ' + device.uid : device.uid
                    }
                    primaryTypographyProps={{ variant: 'body2' }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                  <ListItemSecondaryAction>
                    <IconButton size='small' onClick={(e) => deleteClick(e, device)}>
                      <DeleteIcon fontSize='small' />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItemButton>
              ))
            }
          </List>
        </Grid>
        <Grid size={7}>
          <Paper variant='outlined' sx={{ p: 2, borderRadius: 2 }}>
            {!hasDetail ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant='body2' color='text.secondary'>{t('selectDevice')}</Typography>
              </Box>
            ) : (
              <Grid container spacing={2}>
                <Grid size={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography variant='subtitle1' sx={{ fontWeight: 600 }}>{selectedDevice.name || t('none')}</Typography>
                    <Chip
                      label={selectedDevice.status}
                      size='small'
                      sx={{
                        bgcolor: statusColor(selectedDevice.status),
                        color: selectedDevice.status === 'disconnected' ? 'text.secondary' : '#fff',
                        fontWeight: 500, fontSize: '0.7rem'
                      }}
                    />
                  </Box>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 0.5, rowGap: 0.25 }}>
                    <Typography variant='caption' color='text.secondary'>{t('deviceType')}:</Typography>
                    <Typography variant='caption'>{selectedDevice.deviceType || t('none')}</Typography>
                    <Typography variant='caption' color='text.secondary'>{t('connection')}:</Typography>
                    <Typography variant='caption'>{selectedDevice.connection || t('none')}</Typography>
                    <Typography variant='caption' color='text.secondary'>{t('address')}:</Typography>
                    <Typography variant='caption'>{selectedDevice.uid || t('none')}</Typography>
                    <Typography variant='caption' color='text.secondary'>{t('manufacturer')}:</Typography>
                    <Typography variant='caption'>{selectedDevice.manufacturer || t('none')}</Typography>
                    <Typography variant='caption' color='text.secondary'>{t('deviceDriver')}:</Typography>
                    <Typography variant='caption'>{selectedDevice.provider || t('none')}</Typography>
                    <Typography variant='caption' color='text.secondary'>{t('version')}:</Typography>
                    <Typography variant='caption'>{selectedDevice.version || t('none')}</Typography>
                    <Typography variant='caption' color='text.secondary'>{t('Ready to Print')}:</Typography>
                    <Typography variant='caption'>
                      <Box component='span' sx={{
                        display: 'inline-block', width: 8, height: 8, borderRadius: '50%', mr: 0.5,
                        bgcolor: statusDotColor(deviceStatuses[selectedDevice.uid], resolutionMismatch),
                        verticalAlign: 'middle'
                      }} />
                      {deviceStatuses[selectedDevice.uid]?.status || t('checking')}
                    </Typography>
                    <Typography variant='caption' color='text.secondary'>{t('detectedResolution')}:</Typography>
                    <Typography variant='caption'>
                      {detectedResolution != null
                        ? `${detectedResolution.dpi} DPI (${detectedResolution.dpmm} dpmm)`
                        : t('none')}
                    </Typography>
                  </Box>
                  {deviceStatuses[selectedDevice.uid]?.errors && (
                    <Typography variant='caption' color='error.main' sx={{ mt: 0.5, display: 'block' }}>
                      {deviceStatuses[selectedDevice.uid].errors.join(', ')}
                    </Typography>
                  )}
                </Grid>

                <Grid size={12}><Divider /></Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <SelectField
                    error={errors[t('quality')] != null ? true : false}
                    helperText={errors[t('quality')]}
                    label={t('printerQuality')}
                    name='quality'
                    options={values.labelPrintQualities}
                    onChange={changeQuality}
                    value={selectedDevice.quality || ''}
                    defaultValue={null}
                    data-cy='printerQuality'
                  />
                  {resolutionMismatch &&
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                      <Typography variant='caption' color='error.main'>
                        {t('printerResolutionMismatch')}
                      </Typography>
                      <Button size='small' variant='outlined' color='error' onClick={applyDetectedQuality}
                        sx={{ fontSize: '0.7rem', py: 0, flexShrink: 0 }} data-cy='useDetectedQuality'>
                        {t('useDetected')}
                      </Button>
                    </Box>
                  }
                </Grid>
                <Grid size={{ xs: 12, sm: 3 }}>
                  <TextField value={selectedDevice.pauseCount} onChange={changeValue} name='pauseCount'
                    size='medium' fullWidth disabled={false} label={t('pauseCount')} type='number' data-cy='pauseCount'
                    error={errors[t('pauseCount')] != null ? true : false} helperText={errors[t('pauseCount')]} />
                </Grid>

                <Grid size={12}><Divider /></Grid>

                <Grid size={12}>
                  <Typography variant='subtitle2' color='text.secondary' sx={{ mb: 1 }}>{t('labels')}</Typography>
                  <Box sx={{ maxHeight: 250, overflow: 'auto' }}>
                    {values.labelPrintTypes != null && values.labelPrintTypes.length > 0 &&
                      values.labelPrintTypes.map((type, index) => {
                        const deviceLabel = selectedDevice.labels != null
                          ? selectedDevice.labels.find(lbl => lbl.type === type.value)
                          : null;
                        const allTemplates = values.templatesByType[type.value] || [];
                        const qualityItem = values.labelPrintQualities?.find(q => q.value === selectedDevice.quality);
                        const dpi = dpmmToDpi(qualityItem?.label);
                        const filteredTemplates = dpi ? allTemplates.filter(tpl => tpl.dpi === dpi) : allTemplates;
                        const options = filteredTemplates.map(tpl => ({ label: tpl.name, value: tpl._id }));
                        const currentValue = deviceLabel?.templateId || '';
                        return (
                          <Paper key={'labelType-' + index} variant='outlined'
                            sx={{ p: 1.5, mb: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                            <Grid container spacing={1.5}>
                              <Grid size={7}>
                                <SelectField
                                  error={errors[type.label] != null ? true : false}
                                  helperText={errors[type.label]}
                                  label={type.label}
                                  name={type.label.toLowerCase()}
                                  options={options}
                                  onChange={(e) => selectLabel(e, type.value)}
                                  value={currentValue}
                                  fullWidth
                                  data-cy={type.label}
                                  disabled={options.length === 0}
                                />
                              </Grid>
                              <Grid size={{ xs: 5, sm: 3 }} offset={{ sm: 2 }}>
                                <TextField value={deviceLabel?.count ?? 1}
                                  onChange={(e) => changeLabelValue(type.value, 'count', parseInt(e.target.value) || 1)}
                                  name='count' size='medium' fullWidth
                                  disabled={options.length === 0 || !!deviceLabel?.promptForCount ||
                                    (type.value === authConstants.labelPrintTypes?.plant && !!deviceLabel?.eachPlant)}
                                  label={t('count')} type='number' error={errors[t('count')] != null ? true : false} helperText={errors[t('count')]} />
                              </Grid>
                              <Grid size={6}>
                                <CheckboxField data-cy='promptForCount'
                                  disabled={options.length === 0 || (type.value === authConstants.labelPrintTypes?.plant && !!deviceLabel?.eachPlant)}
                                  checked={!!deviceLabel?.promptForCount} onChange={() => changeBool(type.value, 'promptForCount')}
                                  label={t('promptForCount')} name='promptForCount'
                                />
                              </Grid>
                              <Grid size={6}>
                                <CheckboxField data-cy='labelPreview' disabled={options.length === 0}
                                  checked={!!deviceLabel?.labelPreview} onChange={() => changeBool(type.value, 'labelPreview')}
                                  label={t('labelPreview')} name='labelPreview'
                                />
                              </Grid>
                              {type.value === authConstants.labelPrintTypes?.plant &&
                                <Grid size={12}>
                                  <CheckboxField data-cy='oneForEachPlant'
                                    checked={!!deviceLabel?.eachPlant} onChange={() => changeBool(type.value, 'eachPlant')}
                                    label={t('oneForEachPlant')} name='oneForEachPlant'
                                  />
                                </Grid>
                              }
                            </Grid>
                          </Paper>
                        );
                      })
                    }
                  </Box>
                </Grid>

                {isConnected &&
                  <Grid size={12}>
                    <DeviceDiagnostics device={selectedDevice} templates={assignedTemplates} disabled={!sweepDone}
                      status={selectedStatus} />
                  </Grid>
                }

                <Grid size={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, pt: 1 }}>
                    <Button variant='text' color='secondary' disabled={!enableSave} onClick={cancelDevice}>{t('cancel')}</Button>
                    <Button variant='contained' color='primary' disabled={!enableSave} onClick={saveDevice}>
                      {!enableSave ? <CometSpinner stroke="inherit" size={18} /> : null}
                      {t('save')}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            )}
          </Paper>
        </Grid>
      </Grid>
    </ModalDialog>
  );
};

export default DeviceManager;
